import React, { useState, useCallback, useRef, useEffect } from 'react'
import { Button, Spinner, Alert, ProgressBar } from 'react-bootstrap'
import {
  Upload,
  Video,
  X,
  Zap,
  ArrowLeft,
  Play,
  Pause,
  Download,
  RefreshCw,
  CheckCircle,
  AlertCircle,
} from 'lucide-react'
import styled from 'styled-components'
import mpegts from 'mpegts.js'
import { useAppSelector } from '../../../store/hooks'
import { selectSelectedProjectId } from '../../../store/slices/projectSlice'
import { useFetchModelsQuery } from '../../../services/modelsApi'
import { createStreamSession, testConnection } from '../../../services/videoInferenceApi.temp'

const ToolContainer = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 2rem;
  overflow-y: auto;
  overflow-x: hidden;
  gap: 1.5rem;
`

const HeaderSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
`

const TitleGroup = styled.div`
  h2 {
    font-size: 1.75rem;
    font-weight: 600;
    color: #fff;
    margin-bottom: 0.25rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  p {
    font-size: 0.95rem;
    color: rgba(255, 255, 255, 0.6);
    margin: 0;
  }
`

const ContentArea = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: 1.5rem;
  min-height: 0;
`

const LeftPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  min-height: 0;
`

const VideoPreviewCard = styled.div<{ $hasVideo: boolean }>`
  flex: 1;
  background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
  border: 1px solid rgba(102, 126, 234, 0.2);
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;

  video {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`

const DropZone = styled.div<{ $isDragging: boolean }>`
  position: absolute;
  inset: 0;
  border: 2px dashed
    ${({ $isDragging }) => ($isDragging ? 'rgba(102, 126, 234, 0.8)' : 'rgba(102, 126, 234, 0.3)')};
  border-radius: 16px;
  background: ${({ $isDragging }) => ($isDragging ? 'rgba(102, 126, 234, 0.15)' : 'transparent')};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    border-color: rgba(102, 126, 234, 0.6);
    background: rgba(102, 126, 234, 0.05);
  }
`

const DropContent = styled.div`
  text-align: center;
  pointer-events: none;

  .upload-icon {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #ffffff;
    margin: 0 auto 1.5rem;
    box-shadow: 0 8px 24px rgba(102, 126, 234, 0.4);
  }

  h3 {
    font-size: 1.5rem;
    font-weight: 600;
    color: #fff;
    margin-bottom: 0.75rem;
  }

  p {
    font-size: 1rem;
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 0.5rem;
  }

  .formats {
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.5);
    margin-top: 1rem;
  }
`

const VideoControls = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 1rem;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.8), transparent);
  display: flex;
  align-items: center;
  gap: 1rem;
  opacity: 0;
  transition: opacity 0.3s;

  ${VideoPreviewCard}:hover & {
    opacity: 1;
  }
`

const VideoInfo = styled.div`
  position: absolute;
  top: 1rem;
  left: 1rem;
  right: 1rem;
  background: rgba(0, 0, 0, 0.7);
  padding: 0.75rem 1rem;
  border-radius: 8px;
  backdrop-filter: blur(10px);
  display: flex;
  justify-content: space-between;
  align-items: center;

  .filename {
    font-size: 0.9rem;
    color: #fff;
    font-weight: 500;
  }

  .remove-btn {
    background: rgba(220, 38, 38, 0.9);
    border: none;
    border-radius: 6px;
    padding: 0.25rem 0.75rem;
    color: white;
    font-size: 0.85rem;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    &:hover {
      background: rgba(220, 38, 38, 1);
    }
  }
`

const RightPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`

const SideCard = styled.div`
  background: linear-gradient(145deg, #2d3748 0%, #1a202c 100%);
  border: 1px solid rgba(102, 126, 234, 0.2);
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);

  h4 {
    font-size: 1rem;
    font-weight: 600;
    color: #fff;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    &::before {
      content: '';
      width: 4px;
      height: 20px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      border-radius: 2px;
    }
  }
`

const ModelSelect = styled.select`
  width: 100%;
  padding: 0.75rem;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(102, 126, 234, 0.3);
  border-radius: 8px;
  color: #fff;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: rgba(102, 126, 234, 0.5);
  }

  &:focus {
    outline: none;
    border-color: rgba(102, 126, 234, 0.8);
  }

  option {
    background: #1a202c;
  }
`

const StatRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  &:last-child {
    border-bottom: none;
  }

  .label {
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.6);
  }

  .value {
    font-size: 1rem;
    font-weight: 600;
    color: rgba(102, 126, 234, 1);
  }
`

const FrameSkipControl = styled.div`
  margin-top: 1rem;

  label {
    display: block;
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 0.5rem;
  }

  input {
    width: 100%;
    padding: 0.5rem;
    background: rgba(0, 0, 0, 0.3);
    border: 1px solid rgba(102, 126, 234, 0.3);
    border-radius: 8px;
    color: #fff;
    font-size: 0.9rem;

    &:focus {
      outline: none;
      border-color: rgba(102, 126, 234, 0.8);
    }
  }

  .help-text {
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.5);
    margin-top: 0.25rem;
  }
`

const ProcessingOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(5px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  gap: 1rem;
  padding: 2rem;
  text-align: center;

  .status-text {
    font-size: 1.2rem;
    font-weight: 600;
    color: #fff;
  }

  .status-subtext {
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.7);
  }

  .progress-container {
    width: 100%;
    max-width: 400px;
  }
`

const ResultsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;
`

const ActionButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  width: 100%;
`

// Narrow React-Bootstrap Button's very large union props to simplify TS types
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ButtonBase = Button as unknown as React.ComponentType<any>

const SecondaryButton = styled(ButtonBase)`
  flex: 1;
  padding: 0.75rem;
  background: rgba(102, 126, 234, 0.15);
  border: 1px solid rgba(102, 126, 234, 0.3);
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba(102, 126, 234, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    background: rgba(102, 126, 234, 0.25);
    border-color: rgba(102, 126, 234, 0.5);
    color: #fff;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const RunButton = styled(ButtonBase)`
  width: 100%;
  padding: 1rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(102, 126, 234, 0.5);
    background: linear-gradient(135deg, #764ba2 0%, #667eea 100%);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const HiddenInput = styled.input`
  display: none;
`

interface VideoInferenceToolProps {
  onBack: () => void
}

const VideoInferenceTool: React.FC<VideoInferenceToolProps> = ({ onBack }) => {
  const [isDragging, setIsDragging] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [videoUrl, setVideoUrl] = useState<string | null>(null)
  const [selectedModel, setSelectedModel] = useState<string>('')
  const [isPlaying, setIsPlaying] = useState(false)
  const [frameSkip, setFrameSkip] = useState(1)
  const [isStreaming, setIsStreaming] = useState(false)
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [jobId, setJobId] = useState<string | null>(null)
  const [streamStatus, setStreamStatus] = useState<string>('Waiting')
  const [activeStreamUrl, setActiveStreamUrl] = useState<string | null>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamVideoRef = useRef<HTMLVideoElement>(null)
  const mpegtsPlayerRef = useRef<mpegts.Player | null>(null)
  const selectedProjectId = useAppSelector(selectSelectedProjectId)

  const { data: modelsData } = useFetchModelsQuery(
    { project_id: selectedProjectId || '' },
    { skip: !selectedProjectId }
  )

  // Only show models that are deployed (IN_SERVICE status)
  const deployedModels =
    modelsData?.models?.filter((m) => m.deployment?.status === 'IN_SERVICE') || []

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }, [])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const files = Array.from(e.dataTransfer.files).filter(
      (file) => file.type.startsWith('video/') || file.name.toLowerCase().endsWith('.ts')
    )

    if (files.length > 0) {
      setFile(files[0])
    }
  }, [])

  const setFile = (file: File) => {
    setSelectedFile(file)
    const url = URL.createObjectURL(file)
    setVideoUrl(url)
  }

  const handleClick = () => {
    if (!selectedFile) {
      document.getElementById('video-file-input')?.click()
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []).filter(
      (file) => file.type.startsWith('video/') || file.name.toLowerCase().endsWith('.ts')
    )

    if (files.length > 0) {
      setFile(files[0])
    }
  }

  // Test connection on mount
  useEffect(() => {
    const checkConnection = async () => {
      const result = await testConnection()
      console.log('Video Inference Service Status:', result)
      if (!result.connected) {
        console.warn('⚠️ Video inference service is not available:', result.message)
      }
    }
    checkConnection()
  }, [])

  // Cleanup URLs and HLS instance on unmount
  useEffect(() => {
    return () => {
      if (videoUrl) {
        URL.revokeObjectURL(videoUrl)
      }
      if (mpegtsPlayerRef.current) {
        mpegtsPlayerRef.current.destroy()
        mpegtsPlayerRef.current = null
      }
    }
  }, [videoUrl])

  // Cleanup HLS when streaming stops
  useEffect(() => {
    if (!isStreaming && mpegtsPlayerRef.current) {
      mpegtsPlayerRef.current.destroy()
      mpegtsPlayerRef.current = null
    }
  }, [isStreaming])

  useEffect(() => {
    const video = streamVideoRef.current
    if (!video || !activeStreamUrl) return

    video.muted = true
    video.playsInline = true
    video.autoplay = true
    video.crossOrigin = 'anonymous'

    const handleLoadStart = () => console.log('Stream load started')
    const handleLoadedMetadata = () => {
      console.log('Stream metadata loaded')
      setStreamStatus('Live')
    }
    const handleCanPlay = () => {
      console.log('Stream can play, attempting playback')
      video
        .play()
        .then(() => {
          console.log('Stream playback started')
          setIsPlaying(true)
        })
        .catch((err) => {
          console.error('Stream play() error:', err)
          setError(`Playback error: ${err.message}`)
        })
    }
    const handleError = () => {
      const mediaError = video.error
      if (mediaError) {
        const errorMessages = [
          'Unknown error',
          'Video loading aborted',
          'Network error (check CORS headers for GET streams)',
          'Video decode error',
          'Video format not supported (ensure MPEG-TS is enabled)',
        ]
        const friendlyMessage =
          errorMessages[mediaError.code] || `Video error code: ${mediaError.code}`
        console.error('Stream video error:', {
          code: mediaError.code,
          message: mediaError.message,
          friendlyMessage,
        })
        setError(`Video error: ${friendlyMessage}`)
        setIsStreaming(false)
        setStreamStatus('Error')
      }
    }
    const handleWaiting = () => console.log('Stream buffering...')
    const handlePlaying = () => {
      console.log('Stream playing')
      setIsPlaying(true)
    }
    const handlePause = () => {
      console.log('Stream paused')
      setIsPlaying(false)
    }

    video.addEventListener('loadstart', handleLoadStart)
    video.addEventListener('loadedmetadata', handleLoadedMetadata)
    video.addEventListener('canplay', handleCanPlay)
    video.addEventListener('error', handleError)
    video.addEventListener('waiting', handleWaiting)
    video.addEventListener('playing', handlePlaying)
    video.addEventListener('pause', handlePause)

    let shouldCleanupSource = false

    if (mpegts.isSupported()) {
      console.log('Using mpegts.js player')
      const player = mpegts.createPlayer(
        {
          type: 'mpegts',
          isLive: true,
          url: activeStreamUrl,
          hasAudio: true,
          hasVideo: true,
        },
        {
          enableWorker: true,
          enableStashBuffer: false,
          autoCleanupSourceBuffer: true,
          lazyLoad: false,
          liveBufferLatencyChasing: true,
        }
      )

      mpegtsPlayerRef.current = player
      player.attachMediaElement(video)
      player.load()
      try {
        const maybePromise = player.play()
        if (maybePromise instanceof Promise) {
          maybePromise.catch((err: Error) => {
            console.error('mpegts.js play error:', err)
            setError(`Playback error: ${err.message}`)
          })
        }
      } catch (err) {
        const error = err as Error
        console.error('mpegts.js play error:', error)
        setError(`Playback error: ${error.message}`)
      }

      player.on(mpegts.Events.ERROR, (event, data) => {
        console.error('mpegts.js error:', event, data)
        setError(`Streaming error: ${data?.type || 'Unknown error'}`)
        setStreamStatus('Error')
      })
    } else {
      console.log('mpegts.js not supported, falling back to native playback')
      video.src = activeStreamUrl
      shouldCleanupSource = true
      video.load()
    }

    return () => {
      if (mpegtsPlayerRef.current) {
        mpegtsPlayerRef.current.destroy()
        mpegtsPlayerRef.current = null
      } else if (shouldCleanupSource) {
        video.pause()
        video.removeAttribute('src')
        video.load()
      }

      video.removeEventListener('loadstart', handleLoadStart)
      video.removeEventListener('loadedmetadata', handleLoadedMetadata)
      video.removeEventListener('canplay', handleCanPlay)
      video.removeEventListener('error', handleError)
      video.removeEventListener('waiting', handleWaiting)
      video.removeEventListener('playing', handlePlaying)
      video.removeEventListener('pause', handlePause)
    }
  }, [activeStreamUrl])

  const removeVideo = () => {
    if (videoUrl) {
      URL.revokeObjectURL(videoUrl)
    }
    if (mpegtsPlayerRef.current) {
      mpegtsPlayerRef.current.destroy()
      mpegtsPlayerRef.current = null
    }
    setSelectedFile(null)
    setVideoUrl(null)
    setActiveStreamUrl(null)
    setIsPlaying(false)
    setIsStreaming(false)
    setError(null)
    setProgress(0)
    setJobId(null)
    setStreamStatus('Waiting')
  }

  const togglePlay = () => {
    const targetVideo = isStreaming ? streamVideoRef.current : videoRef.current
    if (targetVideo) {
      if (isPlaying) {
        targetVideo.pause()
      } else {
        targetVideo.play()
      }
      setIsPlaying(!isPlaying)
    }
  }

  const handleRunInference = async () => {
    if (!selectedFile) return

    setIsStreaming(true)
    setError(null)
    setProgress(0)
    setStreamStatus('Uploading...')

    try {
      console.log('Starting video streaming inference...', {
        fileName: selectedFile.name,
        fileSize: selectedFile.size,
        frameSkip,
      })

      // Create stream session by uploading video
      const streamSession = await createStreamSession(selectedFile, frameSkip)
      setJobId(streamSession.stream_id)

      console.log('Stream session created:', streamSession)
      setStreamStatus('Connecting...')

      // Use the full_url from the response
      const streamUrl = streamSession.full_url

      console.log('Connecting to stream:', streamUrl)
      setStreamStatus('Streaming...')

      setActiveStreamUrl(streamUrl)
      setProgress(100)
    } catch (err: any) {
      console.error('Video streaming error:', err)
      const errorMessage = err.message || 'Failed to start video stream. Please try again.'
      setError(errorMessage)
      setIsStreaming(false)
      setStreamStatus('Error')
    }
  }

  const handleStopStreaming = () => {
    if (mpegtsPlayerRef.current) {
      mpegtsPlayerRef.current.destroy()
      mpegtsPlayerRef.current = null
    }
    const video = streamVideoRef.current
    if (video) {
      video.pause()
      video.removeAttribute('src')
      video.load()
    }
    setActiveStreamUrl(null)
    setIsStreaming(false)
    setIsPlaying(false)
    setStreamStatus('Stopped')
  }

  return (
    <ToolContainer>
      <HeaderSection>
        <TitleGroup>
          <h2>
            <Video size={28} />
            Video Inference
          </h2>
          <p>Upload a video to run frame-by-frame object detection</p>
        </TitleGroup>
        <Button
          variant="outline-secondary"
          onClick={onBack}
          className="d-flex align-items-center gap-2"
        >
          <ArrowLeft size={16} />
          Back
        </Button>
      </HeaderSection>

      <ContentArea>
        <LeftPanel>
          <VideoPreviewCard $hasVideo={!!selectedFile}>
            {isStreaming && (
              <>
                <video
                  ref={streamVideoRef}
                  controls
                  autoPlay
                  playsInline
                  muted
                  onLoadedMetadata={() => console.log('Stream metadata loaded')}
                  onCanPlay={() => console.log('Stream can play')}
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                  onError={(e) => {
                    console.error('Video playback error:', e)
                    const videoEl = e.target as HTMLVideoElement
                    if (videoEl.error) {
                      console.error('Video error details:', {
                        code: videoEl.error.code,
                        message: videoEl.error.message,
                      })
                    }
                  }}
                />
                <VideoInfo>
                  <span className="filename">
                    <CheckCircle size={16} style={{ color: '#10b981', marginRight: '0.5rem' }} />
                    Live Stream: {selectedFile?.name}
                  </span>
                  <button className="remove-btn" onClick={handleStopStreaming}>
                    <X size={14} />
                    Stop
                  </button>
                </VideoInfo>
              </>
            )}
            {!isStreaming && videoUrl && (
              <>
                <video ref={videoRef} src={videoUrl} />
                <VideoInfo>
                  <span className="filename">{selectedFile?.name}</span>
                  <button className="remove-btn" onClick={removeVideo}>
                    <X size={14} />
                    Remove
                  </button>
                </VideoInfo>
                <VideoControls>
                  <Button
                    variant="light"
                    size="sm"
                    onClick={togglePlay}
                    className="d-flex align-items-center gap-2"
                  >
                    {isPlaying ? <Pause size={16} /> : <Play size={16} />}
                  </Button>
                </VideoControls>
              </>
            )}
            {!selectedFile && !isStreaming && (
              <DropZone
                $isDragging={isDragging}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={handleClick}
              >
                <DropContent>
                  <div className="upload-icon">
                    <Upload size={40} />
                  </div>
                  <h3>Drag & Drop Video</h3>
                  <p>or click to browse your files</p>
                  <p className="formats">Supported: MP4, AVI, MOV, WEBM, TS (MPEG-TS)</p>
                </DropContent>
              </DropZone>
            )}
            <HiddenInput
              id="video-file-input"
              type="file"
              accept="video/*,.ts"
              onChange={handleFileSelect}
            />
          </VideoPreviewCard>
        </LeftPanel>

        <RightPanel>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              <div className="d-flex align-items-center gap-2">
                <AlertCircle size={20} />
                <div>
                  <strong>Error:</strong> {error}
                </div>
              </div>
            </Alert>
          )}

          {isStreaming && jobId && (
            <Alert variant="success">
              <div className="d-flex align-items-center gap-2">
                <CheckCircle size={20} />
                <div>
                  <strong>Streaming Live!</strong> Real-time inference active
                  <br />
                  <small>Stream ID: {jobId}</small>
                </div>
              </div>
            </Alert>
          )}

          <SideCard>
            <h4>Model Selection (Optional)</h4>
            <ModelSelect
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
              disabled={isStreaming}
            >
              <option value="">Select a deployed model (optional)...</option>
              {deployedModels.length === 0 ? (
                <option value="" disabled>
                  No deployed models available
                </option>
              ) : (
                deployedModels.map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.name}
                  </option>
                ))
              )}
            </ModelSelect>

            <FrameSkipControl>
              <label htmlFor="frame-skip">Frame Skip</label>
              <input
                id="frame-skip"
                type="number"
                min="1"
                max="30"
                value={frameSkip}
                onChange={(e) => setFrameSkip(Math.max(1, parseInt(e.target.value) || 1))}
                disabled={isStreaming}
              />
              <div className="help-text">
                Process every Nth frame (higher = faster, lower accuracy)
              </div>
            </FrameSkipControl>
          </SideCard>

          <SideCard>
            <h4>Summary</h4>
            <StatRow>
              <span className="label">Video</span>
              <span className="value">{selectedFile ? '✓' : '—'}</span>
            </StatRow>
            <StatRow>
              <span className="label">Model (Optional)</span>
              <span className="value">{selectedModel ? '✓' : '—'}</span>
            </StatRow>
            <StatRow>
              <span className="label">Frame Skip</span>
              <span className="value">{frameSkip}</span>
            </StatRow>
            <StatRow>
              <span className="label">Status</span>
              <span className="value">{streamStatus}</span>
            </StatRow>
          </SideCard>

          {!isStreaming ? (
            <RunButton disabled={!selectedFile} onClick={handleRunInference}>
              <Zap size={20} />
              Start Streaming
            </RunButton>
          ) : (
            <ResultsSection>
              {activeStreamUrl && (
                <SecondaryButton
                  as="a"
                  href={activeStreamUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-center"
                >
                  <Play size={18} />
                  Open Stream in New Tab
                </SecondaryButton>
              )}

              <RunButton variant="danger" onClick={handleStopStreaming}>
                <X size={20} />
                Stop Streaming
              </RunButton>
            </ResultsSection>
          )}
        </RightPanel>
      </ContentArea>
    </ToolContainer>
  )
}

export default VideoInferenceTool
